import React, { ReactElement, useMemo } from 'react'
import { Control, Controller, FieldErrors } from 'react-hook-form'

import { ChargePointImport, EarthingSystem } from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'
import { transformLBCurrentInput } from '../../../../../common/utils/load-balancer-current-input-transform'
import { isChargingConnectorImport } from '../../../../../lib/findChargingConnector'
import { isHaloImport } from '../../../../../lib/isHalo'
import { CHARGEPOINT_MIN_CURRENT } from '../../../../../common/domain/electrical-settings'
import { isAuraImport } from '../../../../../lib/isAura'
import { getChargerPhaseListByImport } from '../../../../../common/utils/chargePointUtils'
import { Input } from '../../../../../common/uikit/Input'
import { Dropdown } from '../../../../../common/uikit/Dropdown'

type LoadBalancerOfflineCurrentFieldName = `lbc_${number}_${string}`
type LoadBalancerOfflinePhaseFieldName = `lbOfflinePhase_${number}`
type LoadBalancerOfflineSettingsFieldName = LoadBalancerOfflineCurrentFieldName | LoadBalancerOfflinePhaseFieldName

export type LoadBalancerOfflineSettingsFormValues = Partial<
    Record<LoadBalancerOfflineSettingsFieldName, string | number | null | undefined>
>

type ChargePointSettingsType = {
    singleCharger: boolean
    chargePoint: ChargePointImport
    chargerType?: string
    minInstallationCurrent?: number
    earthingSystem?: EarthingSystem | null
    control: Control<LoadBalancerOfflineSettingsFormValues>
    errors: FieldErrors<LoadBalancerOfflineSettingsFormValues>
}

export default ({
    singleCharger,
    chargePoint,
    minInstallationCurrent,
    chargerType,
    earthingSystem,
    control,
    errors,
}: ChargePointSettingsType): ReactElement => {
    const text = useText()

    const connector = chargePoint?.connectors?.find((connector) => connector?.connectorId === 1)
    const installationPhaseMode = useMemo(() => connector?.installationPhaseMode, [connector])

    const phasesList = getChargerPhaseListByImport(chargePoint, earthingSystem, installationPhaseMode)
    const phasesOptions = phasesList.map((phase) => ({ value: phase, label: phase }))
    const getOfflineCurrentFieldName = (connectorId?: number): LoadBalancerOfflineCurrentFieldName =>
        `lbc_${connectorId ?? 1}_${chargerType ?? 'charger'}`
    const getOfflinePhaseFieldName = (connectorId?: number): LoadBalancerOfflinePhaseFieldName =>
        `lbOfflinePhase_${connectorId ?? 1}`
    const getErrorMessage = (fieldName: LoadBalancerOfflineSettingsFieldName): string | undefined => {
        const message = errors[fieldName]?.message

        return typeof message === 'string' ? message : undefined
    }

    return (
        <div className="flex flex-column gap-4">
            {chargePoint?.connectors
                ?.filter(
                    (connector) =>
                        (isChargingConnectorImport(connector) && !isHaloImport(chargePoint)) ||
                        (isHaloImport(chargePoint) && connector.connectorId === 1)
                )
                ?.map(
                    (connector, index) =>
                        connector && (
                            <div
                                className="flex flex-column gap-3"
                                key={`${chargerType ?? 'charger'}-${connector?.connectorId}-${connector?.name}-${index}`}
                            >
                                {isAuraImport(chargePoint) && (
                                    <h4 className="text-base font-semibold m-0">
                                        {connector?.connectorId === 1 ? text('lblLeftSocket') : text('lblRightSocket')}
                                    </h4>
                                )}
                                <div className="grid row-gap-3">
                                    <div className="col-12 lg:col-6">
                                        <Controller
                                            name={getOfflineCurrentFieldName(connector?.connectorId)}
                                            control={control}
                                            rules={{
                                                required: {
                                                    value: true,
                                                    message: text('msgLBOfflineCurrent'),
                                                },
                                                max: {
                                                    value: minInstallationCurrent as number,
                                                    message: text('lblInstallationCurrentOfflineValidationMax', {
                                                        connectorType:
                                                            connector?.connectorId == 1
                                                                ? chargePoint?.connectors &&
                                                                  chargePoint?.connectors[0]?.name
                                                                : chargePoint?.connectors &&
                                                                  chargePoint?.connectors[1]?.name,
                                                        installationCurrent: minInstallationCurrent,
                                                    }),
                                                },
                                                min: {
                                                    value: CHARGEPOINT_MIN_CURRENT,
                                                    message: text('lblInstallationCurrentOfflineValidation', {
                                                        connectorType:
                                                            connector?.connectorId == 1
                                                                ? chargePoint?.connectors &&
                                                                  chargePoint?.connectors[0]?.name
                                                                : chargePoint?.connectors &&
                                                                  chargePoint?.connectors[1]?.name,
                                                        minCurrent: CHARGEPOINT_MIN_CURRENT,
                                                    }),
                                                },
                                            }}
                                            render={({ field: { onBlur, onChange, value } }) => (
                                                <Input
                                                    id={`lbc_${connector?.connectorId}_${chargerType}`}
                                                    label={text('lblLoadBalancerOfflineCurrentLimitField')}
                                                    infoText={text('msgLoadBalancerOfflineCurrentLimitField')}
                                                    type="number"
                                                    required
                                                    data-testid={
                                                        'lbOfflineCurrent-input-' +
                                                        connector?.name +
                                                        connector?.connectorId
                                                    }
                                                    onBlur={onBlur}
                                                    onChange={(event) => {
                                                        const transformedValue =
                                                            event.target.value === ''
                                                                ? event.target.value
                                                                : transformLBCurrentInput.input(event.target.value)

                                                        onChange(transformedValue)
                                                    }}
                                                    error={getErrorMessage(
                                                        getOfflineCurrentFieldName(connector?.connectorId)
                                                    )}
                                                    value={value ?? ''}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div className="col-12 lg:col-6">
                                        <Controller
                                            name={getOfflinePhaseFieldName(connector?.connectorId)}
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <Dropdown
                                                    id={`lbOfflinePhase_${connector.name}`}
                                                    label={text('lblLoadBalancerOfflinePhase')}
                                                    infoText={text('msgLoadBalancerOfflinePhase')}
                                                    disabled={!singleCharger}
                                                    options={phasesOptions}
                                                    value={(value as string | number | null) ?? null}
                                                    onChange={(event) => {
                                                        onChange(event.value)
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                )}
        </div>
    )
}
